import React, { forwardRef, useEffect, useMemo, useRef, /*useImperativeHandle, useRef,*/ useState } from 'react'
import { SIMILAR_FINDINGS_TYPES, FINDING_ACTIONS, FINDING_STATUS, FINDING_UPDATE_TASK_TYPES, SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES, TOOLS_NOT_FOR_FUTURE_FINDINGS } from './Constants';
import { CellRenderers } from "utils/tableUtils";
import { Form, Spin, /*Alert,*/ Tooltip } from 'antd';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import BaseModal from 'components/BaseModal';
import ModalUtil from 'utils/modalUtil';
import _, { isEmpty, lowerCase, noop } from 'lodash';
// import TicketIdWithStatus from 'components/TicketIdWithStatus';
import FindingAPI from 'restAPI/FindingAPI';
import Vocabulary from 'components/Vocabulary';
import AppLoader from 'components/AppLoader';
// import AuthorisedContainer from 'components/AuthorisedContainer';
import { PermissionSetMap } from 'constants/RBACConstants';
import { hasPermission } from 'utils/authService';
// import AuthorisedCheckbox from 'components/AuthorisedCheckbox';
// import { getTicketSystemName } from './utils';
import message from 'components/CustomMessage';
import { MarkdownFormatter } from "components/MarkupFormatter";
import { OnSuccess, OnError } from "./AcceptRiskPopup";
import { MODAL_SIZE_ULTRA } from 'constants/Layout';
import SimilarFindingsTable from './SimilarFindingsTable';
import { useFindingScopeFields } from 'views/Findings/utils';

const SuppressedPopup = forwardRef(({finding, ticketDetails, afterClose, action, onOk, featureFlags}, ref) => {
    const [form] = Form.useForm();
    const {/*shouldConfigureOrEnableJira,*/ ticketId} = ticketDetails;
    const findingUpdateTaskType = action === FINDING_ACTIONS.SUPPRESSED ? FINDING_UPDATE_TASK_TYPES.SUPPRESSED : FINDING_UPDATE_TASK_TYPES.OPEN;
    

    // const [isCreateTicket, /*setIsCreateTicket*/] = useState(hasPermission(PermissionSetMap.CREATE_TICKETS) && !shouldConfigureOrEnableJira && !isTicketCreationInProgress);
    const [similarFindings, setSimilarFindings] = useState(0);
    const [loading, setLoading] = useState(false);
    const [scope, setScope] = useState('FINDING');
    const [rule, setRule] = useState(undefined);
    const [showFindingsTable, setShowFindingsTable] = useState(false);
    const similarFindingsToNotUpdate = useRef([]);
    const [updateType, setUpdateType] = useState(SIMILAR_FINDINGS_TYPES.RULE_BASED);
    const [tagCondition, setTagCondition] = useState('EQUALS');

    useEffect(() => {
        if(!scope || scope === 'FINDING' || !similarFindings){
            setRule(undefined);
            return noop;
        }
        const { promise, cancel } = FindingAPI.similarFindings.getRule({ 
            findingId: finding.id,
            taskType: action === FINDING_ACTIONS.NOT_SUPPRESSED ? 'open' : 'suppressed',
            scope,
        });
        promise.then(setRule);
        return cancel;
    }, [finding.id, scope, action, similarFindings]);

    useEffect(() => {
        if(!isEmpty(rule)) {
            form.setFieldsValue({
                applyOnFutureFindings: false
            });
        }
    },[rule, form]);
    
    const fileList = [];
    const handleOnOk = async () => {
        await form.validateFields();
            let data = form.getFieldsValue();
            data = {
                ...data,
                file: fileList
            }
            
            if(!_.isEmpty(ticketId)){
                data.createTicket = false;
            }
            const response = {
                ...data,
                id: finding.id, 
                findingId: finding.id,
                scope,
                updateType,
                contains: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains,
                equals: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals,
                status: action === FINDING_ACTIONS.SUPPRESSED ? FINDING_STATUS.SUPPRESSED : FINDING_STATUS.OPEN,
                ...(!_.isEmpty(similarFindingsToNotUpdate.current) ? { ignoreFindingIds: similarFindingsToNotUpdate.current } : {})
            }
            const {successMessage, errorMessage, exceptionType} = await onOk(response, similarFindings, similarFindingsToNotUpdate.current);
            if (successMessage) {
                ModalUtil.show({
                    content: (
                        <OnSuccess message={successMessage} />
                    ),
                    afterClose,
                })
            }  else if(errorMessage){
                ModalUtil.show({
                    content: (
                        <OnError
                            message={errorMessage}
                            response={{
                                ...response,
                                toolName: finding.toolName,
                                productName: finding.product.name,
                                subProductName: finding.subProduct.name,
                                envName: finding.envName,
                                exceptionType,
                            }}
                        />
                    ),
                    afterClose,
                })
            }
            else {
                afterClose();
            }
    }
    
    const scopeOnChange = (_scope) => {
        setScope(_scope);
        if (_scope === 'FINDING') {
            setShowFindingsTable(false);
            setSimilarFindings(0);
        }
    };

    const payload = useMemo(() => ({
        scope: scope,
        updateType,
        contains: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains,
        equals: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals,
        findingId: finding.id,
        findingUpdateTaskType,
    }), [scope, updateType, tagCondition, findingUpdateTaskType, finding])

    useEffect(() => {
        if (scope !== 'FINDING') {
            setLoading(true);
            
            const { promise, cancel } = FindingAPI.similarFindings.count(payload);

            promise.then(response => setSimilarFindings(response || 0)).catch(e => {
                console.error(e);
                if(e.response.data.message.startsWith("Simila")){
                    message.error("Similar Finding Rules cannot be applied to this finding")
                    setSimilarFindings(undefined)
                }
            }).finally(() => setLoading(false));

            return cancel;
        }
    }, [scope, payload]);
    
    const redirectToBucket = () => {
        setShowFindingsTable(true);
    };

    const permission = hasPermission(action === FINDING_ACTIONS.SUPPRESSED ? PermissionSetMap.SIMILAR_FINDINGS_SUPPRESSED : PermissionSetMap.SIMILAR_FINDINGS_REOPEN) ;

    const scopeParams = useMemo(() => ({
        finding,
        helpText: !permission ? `Similar findings ${action === FINDING_ACTIONS.SUPPRESSED ? 'confirm' : 'reopen'} permission is required to change scope.` : '',
        permission,
        scopeOnChange,
        redirectToBucket,
        similarFindings,
        showFindingsTable,
        scope,
        updateType,
        setUpdateType,
        setTagCondition
    }), [finding, permission, scope, similarFindings, updateType, showFindingsTable, action])

    const scopeFields = useFindingScopeFields({payload, ...scopeParams});

    const fields = useMemo(() => {
        const fields = [
            {
                name: "summary",
                label: "Summary",
                type: FieldTypes.LABEL,
                value: <MarkdownFormatter markdown={finding.title} collapsible={true} />,
            },
            {
                name: "note",
                label: "Note",
                type: FieldTypes.TEXT_AREA,
                required: true,
                customLengthRule: {
                    max: 1000,
                    message: "Maximum length is 1000 characters"
                },
                props: {
                    autoFocus: true
                }
            },
        ];

        if (featureFlags.VERSIONS) {
            fields.push({
                label: "Apply to all unresolved versioned Findings",
                name: "applyToVersionFindings",
                type: FieldTypes.SWITCH,
                inline: true,
            });
        }

        fields.push(...scopeFields)

        if(action !== FINDING_ACTIONS.NOT_SUPPRESSED && scope !== 'FINDING' && !_.isUndefined(similarFindings) && TOOLS_NOT_FOR_FUTURE_FINDINGS.indexOf(_.toLower(finding.toolName || finding.source)) === -1){
            if (updateType === SIMILAR_FINDINGS_TYPES.RULE_BASED) {
                fields.push({
                    name: "applyOnFutureFindings",
                    label: <Tooltip title={!isEmpty(rule) ? 
                        `Rule already exists to mark all future findings as ${lowerCase(rule.otherProperties.ACTION_STATUS)}.`
                        : undefined}>Apply to all future findings</Tooltip>,
                    type: FieldTypes.BOOLEAN,
                    disabled: !isEmpty(rule),
                    permission: PermissionSetMap.SIMILAR_FINDINGS_RULE_UPDATE,
                    inline: true,
                });
            }
        }

        const tableprops = {
            title: `${similarFindings} Similar Findings`,
            similarFindingsToNotUpdate,
            scope,
            findingUpdateTaskType,
            sourceFinding: finding.id,
            updateType,
            tagCondition,
        };

        if (showFindingsTable) {
            fields.push({
                name: "findingsTable",
                label: '',
                render: () => <SimilarFindingsTable {...tableprops} />
            });
        }

        return fields;
    }, [action, finding, findingUpdateTaskType, rule, scope, scopeFields, showFindingsTable, similarFindings, tagCondition, updateType, featureFlags.VERSIONS])

    const modalProps = {
        title: <>{finding?.status === FINDING_STATUS.SUPPRESSED ? <>Re-<Vocabulary name = "open"/></> : <>Mark {finding?.id} as <Vocabulary name="suppressed" /></>} {CellRenderers.CUSTOMIZED_SEVERITY(finding.severity)} </>,
        onOk: handleOnOk,
        onCancel: () => afterClose(false),
        // okButtonProps: {
        //     disabled: isCreateTicket && systemError && action === FINDING_ACTIONS.CONFIRMED && scope === 'FINDING',
        // },
        okText: "Submit",
        className: "modal-full-height no-footer",
        okButtonProps: {
            disabled: _.isUndefined(similarFindings)
        },
        width: showFindingsTable ? MODAL_SIZE_ULTRA : undefined,
    }

    return (
        <BaseModal
            modalProps={modalProps}
            ref={ref}
            afterClose={noop}
            >
            <Spin spinning={loading} indicator={<AppLoader />}>
                {/* {isCreateTicket && action === FINDING_ACTIONS.CONFIRMED && scope === 'FINDING' && _.isEmpty(ticketId) && systemError && <Alert className="m-b-xs" type="warning" showIcon message={"Ticket system configuration has errored, ticket creation may fail."} />} */}
                <Form
                    layout="vertical"
                    form={form}
                    initialValues={{
                        // createTicket: isCreateTicket,
                        scope: "FINDING",
                        applyToVersionFindings: true
                    }}
                >
                    {generateFormFields(fields)}
                </Form>
            </Spin>
        </BaseModal>
    )
})

export default SuppressedPopup
